import { useMemo, useState } from 'react';

import { wideBreakpoints } from '@r1-frontend/ui-react/components/layouts/wideContainer';
import DefaultPopup from '@r1-frontend/ui-react/components/popups/defaultPopup';
import { useMatchMedia } from '@r1-frontend/shared/hooks/useMatchMedia';

import LazyCallBack from '~/src/components/LazyCallBack';
import { IMinPrice, ITariffLine } from '~/src/entities/tariff-lines';
import sendClickChooseTariff from '~/src/features/bundles/analytics/sendClickChooseTariff';
import TariffLineOfferCard from '~/src/features/bundles/components/TariffLineOfferCard';
import { MAX_TARIFF_LINES_SHOW } from '~/src/features/bundles/constants';
import { getFinalPriceFromMinPrices } from '~/src/features/bundles/helpers/getFinalPriceFromMinPrices';
import { getFullBuyTariffLink } from '~/src/features/bundles/helpers/getFullBuyTariffLink';
import { getIdentifyDataFromFirstTariff } from '~/src/features/bundles/helpers/getIdentifyDataFromFirstTariff';
import { getProductIDsByMinPrices } from '~/src/features/bundles/helpers/getProductIDsByMinPrices';
import { getTarifflLinesWithSortedShopItems } from '~/src/features/bundles/helpers/getTarifflLinesWithSortedShopItems';
import isFullBuyVillage from '~/src/features/full-buy/helpers/ab/isFullBuyVillage';

import * as ST from './styled';

export interface ITariffLineOffers {
    tariffLines: ITariffLine[],
    isVillage: boolean,
    isLoading: boolean,
}

const TariffLineOffers = ({ tariffLines = [], isVillage, isLoading }: ITariffLineOffers): JSX.Element => {
    const isMobile = useMatchMedia(`(max-width: ${wideBreakpoints.mobile})`);

    const preparedTariffLines = useMemo(() => getTarifflLinesWithSortedShopItems(tariffLines), [tariffLines]);
    const [isRequestPopupOpen, setIsRequestPopupOpen] = useState(false);
    const [selectedTariffName, setSelectedTariffName] = useState('');
    const [selectedMinPrices, setSelectedMinPrices] = useState<IMinPrice[]>();

    const selectTariffHandler = (tariffLine: ITariffLine) => () => {
        if (!isVillage || isFullBuyVillage()) {
            sendClickChooseTariff(tariffLine.alias, isVillage);
            return;
        }

        setIsRequestPopupOpen(true);
        setSelectedMinPrices(tariffLine.minPrices);
        setSelectedTariffName(tariffLine.alias);
        sendClickChooseTariff(tariffLine.alias, true);
    };

    const lazyCallbackCloseHandler = () => {
        setIsRequestPopupOpen(false);
        setSelectedTariffName('');
    };

    return (
        <>
            {isLoading
                ? <ST.StyledThrobber position='absolute'/>
                : preparedTariffLines.length
                    ? (
                        <ST.TariffOffersContainer itemsCount={preparedTariffLines?.length}>
                            {preparedTariffLines.slice(0, MAX_TARIFF_LINES_SHOW).map((tariffLine) => {
                                // @TODO После доработок бека выводить цену с помощью getFinalPriceFromMinPrices()
                                const { salePackageId } = getIdentifyDataFromFirstTariff(tariffLine?.tariffs, isVillage);
                                const price = getFinalPriceFromMinPrices(tariffLine.minPrices, isVillage);
                                const fullBuyTariffLink = (!isVillage || isFullBuyVillage()) ? getFullBuyTariffLink(salePackageId, tariffLine.alias, isVillage) : '';
                                return (
                                    <TariffLineOfferCard
                                        key={tariffLine.id}
                                        img={tariffLine.image_promo}
                                        imgWebp={tariffLine.image_promo_webp}
                                        title={tariffLine.title}
                                        selectButtonLink={fullBuyTariffLink}
                                        priceFrom={price}
                                        onSelectTariff={selectTariffHandler(tariffLine)}
                                        shopItems={tariffLine?.shopItems}
                                    />
                                );
                            })
                            }
                        </ST.TariffOffersContainer>
                    )
                    : <p>Нет доступных линеек</p>
            }

            <DefaultPopup
                isOpen={isRequestPopupOpen}
                onClose={lazyCallbackCloseHandler}
                width={isMobile ? '343px' : '600px'}
            >
                <LazyCallBack
                    tariffName={selectedTariffName}
                    requestDataParams={{ product_id: getProductIDsByMinPrices(selectedMinPrices, isVillage) }}
                    onClose={lazyCallbackCloseHandler}
                    withPopupResult={true}
                    title='Тариф для частного дома можно подключить только по телефону'
                    description={<>Оставьте ваше имя и&nbsp;номер телефона&nbsp;&mdash; мы&nbsp;вам позвоним и&nbsp;обо всём расскажем</>}
                />
            </DefaultPopup>
        </>
    );
};

export default TariffLineOffers;
